import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useConfirmationDialog } from '@/contexts/useConfirmationDialog';
import { AccessGroupsDTO, RolesDTO } from '@redux/identity/identityTypes';
import {
 accessGroupsAddRoleStart,
 accessGroupsDeleteGroupStart,
 accessGroupsGetRolesStart,
 accessGroupsGetRolesTableStart,
 accessGroupsRemoveRoleStart,
 createItemStart,
 getTableStart,
 updateItemStart,
} from '@redux/accessGroups/accessGroupsSlice';
import { RootState } from '@redux/store';

interface AccessGroupFormValues {
 Name: string;
 Description: string;
}

const defaultFormValues: AccessGroupFormValues = {
 Name: '',
 Description: '',
};

const data = {
 limit: 10000,
};

export const useAccessGroups = () => {
 const dispatch = useDispatch();
 const { openDialog } = useConfirmationDialog();

 const { items, roles, allRoles, loading, success, error } = useSelector(
  (state: RootState) =>
   state.accessGroups as typeof state.accessGroups & {
    error?: Record<string, unknown>;
   },
 );

 const accessGroups = items as AccessGroupsDTO[];

 const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
 const [localRoles, setLocalRoles] = useState<RolesDTO[]>([]);
 const [initialRoleState, setInitialRoleState] = useState<
  Record<string, boolean>
 >({});
 const [isSavingRoles, setIsSavingRoles] = useState(false);
 const [modalOpen, setModalOpen] = useState(false);
 const [editingGroup, setEditingGroup] = useState<AccessGroupsDTO | null>(null);
 const [searchValue, setSearchValue] = useState('');

 const roleSaveBatchRef = useRef<{ hasAdd: boolean; hasRemove: boolean }>({
  hasAdd: false,
  hasRemove: false,
 });

 const { control, handleSubmit, reset } = useForm<AccessGroupFormValues>({
  defaultValues: defaultFormValues,
 });

 const getAllAccessGroups = () => {
  dispatch(getTableStart({ ...data, table: 'accessGroups' }));
 };

 useEffect(() => {
  getAllAccessGroups();
 }, [dispatch]);

 useEffect(() => {
  const rolesList = Array.isArray(roles) ? roles : [];
  setLocalRoles(rolesList);
  const nextInitialRoleState = rolesList.reduce<Record<string, boolean>>(
   (acc, role) => {
    if (!role.Id) {
     return acc;
    }
    acc[role.Id] = true;
    return acc;
   },
   {},
  );
  setInitialRoleState(nextInitialRoleState);
  setIsSavingRoles(false);
  roleSaveBatchRef.current = { hasAdd: false, hasRemove: false };
 }, [roles]);

 useEffect(() => {
  if (!selectedGroupId) {
   setLocalRoles([]);
   return;
  }
  dispatch(accessGroupsGetRolesTableStart({ ...data }));
  dispatch(accessGroupsGetRolesStart({ accessGroupId: selectedGroupId }));
 }, [dispatch, selectedGroupId]);

 useEffect(() => {
  if (!isSavingRoles || !selectedGroupId) {
   return;
  }
  const { hasAdd, hasRemove } = roleSaveBatchRef.current;
  const isAddDone = !hasAdd || loading?.accessGroupsAddRole === null;
  const isRemoveDone = !hasRemove || loading?.accessGroupsRemoveRole === null;
  if (!isAddDone || !isRemoveDone) {
   return;
  }
  const hasError =
   (hasAdd && !!error?.accessGroupsAddRole) ||
   (hasRemove && !!error?.accessGroupsRemoveRole);
  roleSaveBatchRef.current = { hasAdd: false, hasRemove: false };
  setIsSavingRoles(false);
  dispatch(accessGroupsGetRolesTableStart({ ...data }));
  dispatch(accessGroupsGetRolesStart({ accessGroupId: selectedGroupId }));
  if (hasError) {
   return;
  }
 }, [
  isSavingRoles,
  selectedGroupId,
  loading?.accessGroupsAddRole,
  loading?.accessGroupsRemoveRole,
  error?.accessGroupsAddRole,
  error?.accessGroupsRemoveRole,
  dispatch,
 ]);

 useEffect(() => {
  if (!success?.createItem && !success?.updateItem) {
   return;
  }
  setModalOpen(false);
  setEditingGroup(null);
  getAllAccessGroups();
 }, [success?.createItem, success?.updateItem, dispatch]);

 useEffect(() => {
  if (!success?.accessGroupsDeleteGroup) {
   return;
  }
  setSelectedGroupId(null);
  setLocalRoles([]);
  getAllAccessGroups();
 }, [success?.accessGroupsDeleteGroup, dispatch]);

 const groupedRoles = useMemo(() => {
  const groups: Record<string, RolesDTO[]> = {};
  allRoles.forEach((role) => {
   const name = role.Name || 'Other';
   if (!groups[name]) {
    groups[name] = [];
   }
   groups[name].push(role);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
 }, [allRoles]);

 const normalizedSearchValue = useMemo(
  () => searchValue.trim().toLowerCase(),
  [searchValue],
 );

 const filteredGroupedRoles = useMemo(() => {
  if (!normalizedSearchValue) {
   return groupedRoles;
  }
  return groupedRoles.reduce<[string, RolesDTO[]][]>(
   (acc, [groupName, groupRolesList]) => {
    const groupMatches = groupName
     .toLowerCase()
     .includes(normalizedSearchValue);
    const filteredRoles = groupMatches
     ? groupRolesList
     : groupRolesList.filter((role) => {
        const roleAction = (role.Action || '').toLowerCase();
        const roleName = (role.Name || '').toLowerCase();
        const roleDescription = (role.Description || '').toLowerCase();
        return (
         roleAction.includes(normalizedSearchValue) ||
         roleName.includes(normalizedSearchValue) ||
         roleDescription.includes(normalizedSearchValue)
        );
       });
    if (groupMatches || filteredRoles.length > 0) {
     acc.push([groupName, filteredRoles]);
    }
    return acc;
   },
   [],
  );
 }, [groupedRoles, normalizedSearchValue]);

 const handleSelectGroup = (group: AccessGroupsDTO) => {
  if (!group.Id) {
   return;
  }
  roleSaveBatchRef.current = { hasAdd: false, hasRemove: false };
  setIsSavingRoles(false);
  setLocalRoles([]);
  setInitialRoleState({});
  setSelectedGroupId(group.Id);
 };

 const handleRoleToggle = (role: RolesDTO) => {
  if (!selectedGroupId || !role.Id || isSavingRoles) {
   return;
  }
  const roleId = role.Id;
  setLocalRoles((prev) => {
   const isAssigned = prev.some((r) => r.Id === roleId);
   if (isAssigned) {
    return prev.filter((r) => r.Id !== roleId);
   }
   return [...prev, { ...role, IsActive: true }];
  });
 };

 const handleGroupToggle = (groupRolesList: RolesDTO[]) => {
  if (!selectedGroupId || isSavingRoles) {
   return;
  }
  const groupRoleIds = groupRolesList
   .map((r) => r.Id)
   .filter((id): id is string => !!id);
  const groupRoleIdSet = new Set(groupRoleIds);

  setLocalRoles((prev) => {
   const prevRoleIds = new Set(
    prev.map((r) => r.Id).filter((id): id is string => !!id),
   );
   const allAssigned = groupRoleIds.every((id) => prevRoleIds.has(id));

   if (allAssigned) {
    return prev.filter((role) => role.Id && !groupRoleIdSet.has(role.Id));
   }

   const rolesToAdd = groupRolesList
    .filter((role) => role.Id && !prevRoleIds.has(role.Id))
    .map((role) => ({ ...role, IsActive: true }));
   return [...prev, ...rolesToAdd];
  });
 };

 const localRoleIdSet = useMemo(
  () => new Set(localRoles.map((r) => r.Id).filter((id): id is string => !!id)),
  [localRoles],
 );

 const pendingRoleChanges = useMemo(() => {
  if (!allRoles || allRoles.length === 0) {
   return { rolesToAdd: [], rolesToRemove: [] };
  }
  return allRoles
   .filter((role): role is RolesDTO => !!role.Id)
   .reduce(
    (acc, roleDef) => {
     const roleId = roleDef.Id as string;
     const wasAssigned = !!initialRoleState[roleId];
     const willBeAssigned = localRoleIdSet.has(roleId);
     if (wasAssigned === willBeAssigned) {
      return acc;
     }
     const role = { ...roleDef, IsActive: willBeAssigned };
     if (willBeAssigned) {
      acc.rolesToAdd.push(role);
      return acc;
     }
     acc.rolesToRemove.push(role);
     return acc;
    },
    { rolesToAdd: [] as RolesDTO[], rolesToRemove: [] as RolesDTO[] },
   );
 }, [allRoles, initialRoleState, localRoleIdSet]);

 const hasRoleChanges =
  pendingRoleChanges.rolesToAdd.length > 0 ||
  pendingRoleChanges.rolesToRemove.length > 0;

 const handleResetRoleChanges = () => {
  if (!selectedGroupId || isSavingRoles || !hasRoleChanges) {
   return;
  }
  setLocalRoles(roles);
 };

 const handleSaveRoleChanges = () => {
  const { rolesToAdd, rolesToRemove } = pendingRoleChanges;
  if (
   !selectedGroupId ||
   isSavingRoles ||
   (rolesToAdd.length === 0 && rolesToRemove.length === 0)
  ) {
   return;
  }
  roleSaveBatchRef.current = {
   hasAdd: rolesToAdd.length > 0,
   hasRemove: rolesToRemove.length > 0,
  };
  setIsSavingRoles(true);
  if (rolesToAdd.length > 0) {
   dispatch(
    accessGroupsAddRoleStart({
     accessGroupId: selectedGroupId,
     roles: rolesToAdd,
    }),
   );
  }
  if (rolesToRemove.length > 0) {
   dispatch(
    accessGroupsRemoveRoleStart({
     accessGroupId: selectedGroupId,
     roles: rolesToRemove,
    }),
   );
  }
 };

 const selectedGroup = accessGroups.find(
  (group) => group.Id === selectedGroupId,
 );

 const handleCopyGroup = () => {
  if (!selectedGroup) {
   return;
  }
  setEditingGroup(null);
  reset({
   Name: selectedGroup.Name ? `${selectedGroup.Name} Copy` : '',
   Description: selectedGroup.Description || '',
  });
  setModalOpen(true);
 };

 const handleOpenCreateModal = () => {
  setEditingGroup(null);
  reset(defaultFormValues);
  setModalOpen(true);
 };

 const handleOpenEditModal = (group: AccessGroupsDTO) => {
  setEditingGroup(group);
  reset({
   Name: group.Name || '',
   Description: group.Description || '',
  });
  setModalOpen(true);
 };

 const handleCloseModal = () => {
  setModalOpen(false);
  setEditingGroup(null);
 };

 const handleFormSubmit = (values: AccessGroupFormValues) => {
  if (editingGroup?.Id) {
   dispatch(
    updateItemStart({
     table: 'accessGroups',
     id: editingGroup.Id,
     data: {
      Id: editingGroup.Id,
      Name: values.Name,
      Description: values.Description || null,
     },
     isPut: true,
    }),
   );
   return;
  }
  dispatch(
   createItemStart({
    table: 'accessGroups',
    data: {
     Name: values.Name,
     Description: values.Description || null,
    },
   }),
  );
 };

 const handleDeleteGroup = (
  group: AccessGroupsDTO,
  title: string,
  message: string,
 ) => {
  if (!group.Id) {
   return;
  }
  openDialog({
   title,
   message,
   onConfirm: () => {
    dispatch(accessGroupsDeleteGroupStart({ id: group.Id as string }));
   },
  });
 };
 const isSubmitting = !!loading?.createItem || !!loading?.updateItem;
 const isLoadingRoles = !!loading?.accessGroupsGetRoles;
 const isLoadingList = !!loading?.getTable;
 const isApplyingRoleChanges =
  isSavingRoles ||
  !!loading?.accessGroupsAddRole ||
  !!loading?.accessGroupsRemoveRole;

 return {
  accessGroups,
  selectedGroup,
  selectedGroupId,
  localRoles,
  localRoleIdSet,
  filteredGroupedRoles,
  searchValue,
  modalOpen,
  editingGroup,
  isSubmitting,
  isLoadingRoles,
  isLoadingList,
  isApplyingRoleChanges,
  hasRoleChanges,
  control,
  handleSubmit,
  setSearchValue,
  handleSelectGroup,
  handleRoleToggle,
  handleGroupToggle,
  handleResetRoleChanges,
  handleSaveRoleChanges,
  handleCopyGroup,
  handleOpenCreateModal,
  handleOpenEditModal,
  handleCloseModal,
  handleFormSubmit,
  handleDeleteGroup,
 };
};

